import {Component, ContentChild, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef} from '@angular/core';
import {ControlValueAccessor, UntypedFormArray, UntypedFormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {MessageTranslateService} from '@shared/services';

@Component({
  selector: 'app-ace-cascading-select',
  templateUrl: './ace-cascading-select.component.html',
  styleUrls: ['./ace-cascading-select.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AceCascadingSelectComponent), multi: true}
  ],
  standalone: false
})
export class AceCascadingSelectComponent implements ControlValueAccessor, OnChanges, OnDestroy {

  @Input() loading = false;

  /**
   * Items the user can select from. The items are expected to be a hierarchy. Each level of the hierarchy will be represented using
   * a select-box control. The hierarchy is built using the values specified in the {@linkcode childPropertyNames} field.
   */
  @Input() items: any[] = [];

  /**
   * Bind Label for each select-box
   */
  @Input() bindLabels: string[] = [];

  /**
   * Bind Value for each select-box
   */
  @Input() bindValues: string[];

  /**
   * Compare With function for each select-box
   */
  @Input() compareWith: Array<(a: any, b: any) => boolean>;

  /**
   * Placeholders for each select-box
   */
  @Input() placeHolders: string[] = [];

  /**
   * Each value in the array represents the name of the object property that holds child values, at that level of the hierarchy.
   * For example, if the items are:
   * ```
   * [{name: 'Parent-1'
   *   children: [{some-property: 'Child-11'
   *               grandchildren: [{another-property: 'Grandchild-111'}
   *                               {another-property: 'Grandchild-112'}]}
   *              {some-property: 'Child-12'
   *               grandchildren: [{another-property: 'Grandchild-122'}]}]}
   * {name: 'Parent-2'
   *  children: [{some-property: 'Child-11'
   *              grandchildren: []}
   *             {some-property: 'Child-12'}]}
   * ]
   * ```
   *
   * {@linkcode childPropertyNames} should be set to ['children', 'grandchildren'] to display the 3-level hierarchy.
   */
  @Input() childPropertyNames: string[] = [];

  /**
   * The number of select-boxes that must have a value, before this control starts having a non-null value. If the number of selected values
   * is less than this number, this control will have a value of null.
   *
   * That is, until the user selects a value from numRequired select-boxes this control will have a value of null. As soon as the user
   * selects {@linkcode numRequired} or more values, this control starts having a not-null value, represented as an array of selected values,
   * one for each select-box. If the user un-selects values such that the number of selected values falls below {@linkcode numRequired}
   * the control will have a value of null again.
   */
  @Input() numRequired = 0;
  @Input() appendTo: string;

  /**
   * Whether the last select-box will allow to select multiple values.
   * If this is true, the array value emitted by this control will have the last entry be an array itself.
   * For example: ['parent', 'child', ['grandchild-1', 'grandchild-2']]
   */
  @Input() multiSelect = false;

  /**
   * This control disables select-boxes, until their parent select-box has a selected value. This is an array of messages that should be
   * shown as tooltip, for each select box, when it is disabled. As the first select-box is never disabled, the first item in this array
   * applies to the second select-box, and so on.
   */
  @Input() dependentDisabledMessages: string[] = [];

  @Output() changeEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() touched: EventEmitter<any> = new EventEmitter<any>();
  @Output() itemsInitialised: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ContentChild('finalOptionTemplate', {static: false}) finalOptionTemplate: TemplateRef<any>;

  // Holds FormControls, one for each select-box
  dropdownArray: UntypedFormArray;

  /**
   * Holds selectable items array for each select box. At each index the items variable stored a Map. The Map at index 0 has only 1 entry
   * in it (with key null) which is the array of items for the first select-box. The Map at each following index (i.e. index >= 1) is keyed
   * by the value of the previous (parent) select-box, and stores the array of selectable items for that value.
   *
   * e.g. If we have the following hierarchy:
   * - Parent-1
   *     - Child-11
   *     - Child-12
   * - Parent-2
   *     - Child-21
   *     - Child-22
   *
   * The {@linkcode itemsHierarchy} variable would look like:
   * [Map([null, ["Parent-1", "Parent-2"]]), Map(["Parent-1", ["Child-11", "Child-12"]], ["Parent-2", ["Child-21", "Child-22"]])]
   * The items Parent-1 and Parent-2 would be shown in the first select-box. As the user selects a parent item from the first select-box,
   * the second select-box would show either [Child-11, Child-12], if the user selected Parent-1 from the first select-box, or
   * [Child-21, Child-22], if the user selected Parent-2.
   */
  itemsHierarchy: Array<Map<any, any[]>> = [];

  private readonly logger: AceLogger;
  private readonly ngUnsubscribe: Subject<void> = new Subject();
  private onChange = (_: any) => {};
  private onTouched = () => {};

  constructor(private translateService: MessageTranslateService, logService: AceLoggerService) {
    this.logger = logService.create(AceCascadingSelectComponent.name);
    this.dropdownArray = new UntypedFormArray([]);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.items || changes.childPropertyNames) {
        this.setupDropdowns(this.items, this.childPropertyNames ? this.childPropertyNames.length + 1 : 1, this.loading);
        this.itemsInitialised.emit(true);
      } else if (changes.numRequired && this.dropdownArray) {
        // Find the index of the first select-box with no value
        const index = this.dropdownArray.controls.findIndex(control => control.disabled || control.value == null);
        if (index < changes.numRequired.currentValue && index >= changes.numRequired.previousValue) {
          // After the change of numRequired, the number of required select-boxes is now greater than the number of selected ones
          // Set the value of the control to null;
          this.publishValueChanged(null);
        } else if (index >= changes.numRequired.currentValue && index < changes.numRequired.previousValue) {
          // After the change of numRequired, the number of required select-boxes is now less than the number of selected ones
          // Set the value of the control;
          this.publishValueChanged(this.getValue());
        }
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(objs: any[]): void {
    if (objs && Array.isArray(objs) && objs.length) {
      if (objs.length <= this.dropdownArray.length) {
        let previousValue: any = null;
        objs.forEach((value, index) => {
          const control = this.dropdownArray.at(index);
          control.setValue(value, {emitEvent: false});
          if (index === 0 || previousValue != null) {
            // Only enable the value if the whole control is not disabled
            if (!this.dropdownArray.disabled) {
              control.enable({emitEvent: false});
            }
          } else {
            control.disable({emitEvent: false});
          }
          previousValue = value;
        });
      } else {
        this.logger.error('The property being set', objs, 'is longer than the number of controls', this.dropdownArray.length);
      }
    } else {
      this.resetValues(0);
    }
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.dropdownArray.disable({emitEvent: false});
    } else {
      for (let i = 0; i < this.dropdownArray.length; i++) {
        // Only enable the first select-box and then select-boxes whose parent has a selected value
        if (i === 0 || this.dropdownArray.at(i - 1).value != null) {
          this.dropdownArray.at(i).enable({emitEvent: false});
        } else {
          // As soon as we find a select box with no selected value, no need to continue
          break;
        }
      }
    }
  }

  onDropdownTouched() {
    this.onTouched();
    this.touched.emit();
  }

  getDisabledTooltip(i: number): string {
    if (i > 0 && this.dropdownArray.at(i).disabled) {
      if (this.dependentDisabledMessages && i < this.dependentDisabledMessages.length && this.dependentDisabledMessages[i - 1] !== undefined) {
        return this.dependentDisabledMessages[i - 1];
      } else if (this.placeHolders && i < this.placeHolders.length && this.placeHolders[i - 1] !== undefined) {
        return this.translateService.instant('Select param first', {param: this.placeHolders[i - 1]});
      }
    }

    return undefined;
  }

  private buildItems(nodes: any[], items: Array<Map<any, any[]>> = [], parent: any = null, depth: number = 0): Array<Map<any, any[]>> {
    let itemMap: Map<any, any[]>;
    if (depth === items.length) {
      itemMap = new Map();
      items.push(itemMap);
    } else if (depth < items.length) {
      itemMap = items[depth];
    } else {
      throw new Error('Depth ' + depth + ' is greater than the length of items ' + items.length);
    }

    const itemArray = [];
    if (nodes && nodes.length) {
      const childPropertyName = depth < this.childPropertyNames.length ? this.childPropertyNames[depth] : null;
      depth++;
      nodes.forEach(node => {
        itemArray.push(node);
        if (childPropertyName) {
          const childrenValue = node[childPropertyName];
          if (childrenValue != null) {
            const children: any[] = Array.isArray(childrenValue) ? childrenValue : [childrenValue];
            if (children.length) {
              this.buildItems(children, items, node, depth);
            }
          }
        }
      });
    }
    itemMap.set(parent, itemArray);

    return items;
  }

  private setupDropdowns(nodes: any[], numDropdowns, loading: boolean): void {
    const currentlyDisabled = this.dropdownArray && this.dropdownArray.disabled;

    const options = {emitEvent: false};
    const items = this.buildItems(nodes || []);
    const nd = numDropdowns != null ? Math.max(numDropdowns, items.length) : items.length;

    // Set the value of existing FormControls that we still need to null
    for (let i = 0; i < Math.min(this.dropdownArray.length, nd); i++) {
      this.dropdownArray.at(i).setValue(null, options);
    }

    // If we need new FormControls, create them
    // Ensure we have at least one select box
    for (let i = this.dropdownArray.length; i < Math.max(nd, 1); i++) {
      const formControl = new UntypedFormControl({value: null, disabled: i > 0 ? true : currentlyDisabled});
      formControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => this.onDropdownValueChanged(value, i));
      this.dropdownArray.push(formControl);
    }

    // If we have too many FormControls, delete the ones that are not needed
    // Never delete the first item (at index 0) to ensure we always show at least one select box
    for (let i = this.dropdownArray.length - 1; i >= Math.max(nd, 1); i--) {
      this.dropdownArray.removeAt(i);
    }

    this.itemsHierarchy = items;
  }

  private onDropdownValueChanged(value: any, index: number) {
    const startIndex = index + 1;
    this.resetValues(startIndex);
    if (index + 1 < this.dropdownArray.length) {
      const control = this.dropdownArray.at(startIndex);
      if (value != null) {
        control.enable({emitEvent: false});
      } else {
        control.disable({emitEvent: false});
      }
    }

    this.publishValueChanged(this.getValue());
  }

  private getValue(): any[] {
    const value: any[] = [];
    for (let i = 0; i < this.dropdownArray.length; i++) {
      const control = this.dropdownArray.at(i);
      const controlValue = control.value;
      // The last component could be a multi-select, which will return either null or empty array as it's "no value" value
      // Therefore, need to check for both
      if (control.disabled || controlValue == null || (Array.isArray(controlValue) && !controlValue.length)) {
        break;
      } else {
        value.push(controlValue);
      }
    }
    return value.length >= this.numRequired ? value : null;
  }

  private publishValueChanged(value: any[]) {
    this.onChange(value);
    this.changeEvent.emit(value);
  }

  private resetValues(startIndex: number) {
    for (let i = startIndex; i < this.dropdownArray.length; i++) {
      const control = this.dropdownArray.at(i);
      if (i > startIndex) {
        control.disable({emitEvent: false});
      }
      control.setValue(null, {emitEvent: false});
    }
  }
}
